import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import axios from 'axios';

const DashboardAreaChart = lazy(() => import('./DashboardSeasonChart'));

// Build seasonData + seasonDrivers from raw chart_rows (same logic as Dashboard.js)
function buildChartData(chartRows) {
  if (!chartRows || chartRows.length === 0) return { seasonData: [], seasonDrivers: [] };

  const driversMap = {};
  chartRows.forEach(r => {
    if (!driversMap[r.driver_id]) {
      driversMap[r.driver_id] = { driver_id: r.driver_id, driver_name: r.driver_name, photo_url: r.photo_url };
    }
  });
  const drivers = Object.values(driversMap);
  const maxWeek = Math.max(...chartRows.map(r => Number(r.week) || 0), 1);
  const running = {};
  drivers.forEach(d => { running[d.driver_id] = 0; });

  const seasonData = Array.from({ length: maxWeek }, (_, i) => {
    const week = i + 1;
    const weekRows = chartRows.filter(r => Number(r.week) === week);
    weekRows.forEach(r => { running[r.driver_id] = r.points || 0; });
    const entry = { week };
    drivers.forEach(d => { entry[String(d.driver_id)] = running[d.driver_id] || 0; });
    return entry;
  });

  drivers.sort((a, b) => (running[b.driver_id] || 0) - (running[a.driver_id] || 0));
  return { seasonData, seasonDrivers: drivers };
}

function SeasonTooltip({ active, payload, label, seasonDrivers }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{ background: 'var(--surface-high)', border: '1px solid var(--outline-variant)', padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
      <div style={{ color: 'var(--on-surface-variant)', marginBottom: 4 }}>KW{label}</div>
      {[...payload].reverse().map((p, i) => {
        const driver = (seasonDrivers || []).find(d => String(d.driver_id) === String(p.dataKey));
        return (
          <div key={i} style={{ color: p.color, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span>{driver?.driver_name?.split(' ')[0] ?? p.dataKey}</span>
            <span>{p.value}</span>
          </div>
        );
      })}
      <div style={{ color: 'var(--on-surface)', marginTop: 4, borderTop: '1px solid var(--outline-variant)', paddingTop: 4 }}>
        ∑ {total}
      </div>
    </div>
  );
}

export default function PastSeasons({ token }) {
const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingArchive, setLoadingArchive] = useState(false);

  const isMobile = window.innerWidth < 768;

  // Load available years
  useEffect(() => {
    if (!token) return;
    axios.get('/api/seasons', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setYears(r.data);
        if (r.data.length > 0) setSelectedYear(r.data[0].season_year);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  // Load selected year's archive
  useEffect(() => {
    if (!selectedYear || !token) return;
    setLoadingArchive(true);
    setArchive(null);
    axios.get(`/api/seasons/${selectedYear}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setArchive(r.data))
      .catch(() => {})
      .finally(() => setLoadingArchive(false));
  }, [selectedYear, token]);

  const { seasonData, seasonDrivers } = useMemo(
    () => buildChartData(archive?.chart_rows || []),
    [archive]
  );

  const totalPoints = useMemo(() => {
    if (!seasonData.length || !seasonDrivers.length) return 0;
    const last = seasonData[seasonData.length - 1];
    return seasonDrivers.reduce((s, d) => s + (last[String(d.driver_id)] || 0), 0);
  }, [seasonData, seasonDrivers]);

  const tooltipEl = useMemo(
    () => <SeasonTooltip seasonDrivers={seasonDrivers} />,
    [seasonDrivers]
  );

  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--outline-variant)',
    borderRadius: 2,
    padding: isMobile ? 16 : 24,
    marginBottom: 16,
  };

  const labelStyle = {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'var(--on-surface-variant)',
    textTransform: 'uppercase',
    marginBottom: 6,
  };

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!loading && years.length === 0) {
    return (
      <div style={{ padding: isMobile ? 16 : 32 }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: isMobile ? 20 : 28, fontWeight: 800, color: 'var(--on-surface)', letterSpacing: '-0.01em', marginBottom: 8 }}>
          VERGANGENE SAISONS
        </div>
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--on-surface-variant)' }}>
            Noch keine archivierten Saisons vorhanden.
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--on-surface-variant)', marginTop: 8, opacity: 0.7 }}>
            Der erste Snapshot wird automatisch am 31. Dezember erstellt.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 900 }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: isMobile ? 20 : 28, fontWeight: 800, color: 'var(--on-surface)', letterSpacing: '-0.01em' }}>
          VERGANGENE SAISONS
        </div>

        {/* Year picker */}
        {years.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {years.map(y => (
              <button
                key={y.season_year}
                onClick={() => setSelectedYear(y.season_year)}
                style={{
                  padding: '5px 14px',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                  border: `1px solid ${selectedYear === y.season_year ? 'var(--brand-primary)' : 'var(--outline-variant)'}`,
                  background: selectedYear === y.season_year ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent',
                  color: selectedYear === y.season_year ? 'var(--brand-primary)' : 'var(--on-surface-variant)',
                  borderRadius: 2,
                  transition: 'all 0.15s',
                }}
              >
                {y.season_year}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40, color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
          Lade…
        </div>
      )}

      {!loading && selectedYear && (
        <>
          {/* ── Stats row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
            <div style={{ ...cardStyle, marginBottom: 0, textAlign: 'center' }}>
              <div style={labelStyle}>Saison</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, color: 'var(--brand-primary)' }}>
                {selectedYear}
              </div>
            </div>
            <div style={{ ...cardStyle, marginBottom: 0, textAlign: 'center' }}>
              <div style={labelStyle}>Team-Punkte</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, color: 'var(--on-surface)' }}>
                {loadingArchive ? '—' : totalPoints}
              </div>
            </div>
            <div style={{ ...cardStyle, marginBottom: 0, textAlign: 'center', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <div style={labelStyle}>Fahrer</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, color: 'var(--on-surface)' }}>
                {loadingArchive ? '—' : (archive?.driver_table?.length ?? 0)}
              </div>
            </div>
          </div>

          {/* ── Chart ── */}
          <div style={cardStyle}>
            <div style={labelStyle}>Punkteverlauf {selectedYear}</div>
            {loadingArchive ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                Lade…
              </div>
            ) : seasonData.length > 0 ? (
              <Suspense fallback={<div style={{ height: 220 }} />}>
                <DashboardAreaChart
                  seasonData={seasonData}
                  seasonDrivers={seasonDrivers}
                  chartEvents={{}}
                  currentWeek={seasonData.length}
                  height={220}
                  gradientPrefix="ps"
                  strokeWidth={1.5}
                  tickFontSize={9}
                  tooltipEl={tooltipEl}
                />
              </Suspense>
            ) : (
              <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                Keine Punktedaten für {selectedYear}
              </div>
            )}
          </div>

          {/* ── Driver table ── */}
          <div style={cardStyle}>
            <div style={labelStyle}>Fahrer-Saison-Endstand {selectedYear}</div>
            {loadingArchive ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Lade…</div>
            ) : (archive?.driver_table?.length > 0) ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--on-surface-variant)', fontWeight: 700, letterSpacing: '0.08em' }}>#</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--on-surface-variant)', fontWeight: 700, letterSpacing: '0.08em' }}>FAHRER</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--on-surface-variant)', fontWeight: 700, letterSpacing: '0.08em' }}>PUNKTE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archive.driver_table.map((driver, idx) => (
                      <tr
                        key={driver.driver_id}
                        style={{ borderBottom: '1px solid var(--outline-variant)', opacity: 0.9 }}
                      >
                        <td style={{ padding: '8px', color: idx === 0 ? 'var(--brand-secondary)' : 'var(--on-surface-variant)', fontWeight: idx === 0 ? 700 : 400 }}>
                          {idx === 0 ? '🏆' : idx + 1}
                        </td>
                        <td style={{ padding: '8px', display: 'flex', alignItems: 'center', gap: 10 }}>
                          {driver.photo_url ? (
                            <img src={driver.photo_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: `1px solid rgba(var(--brand-primary-rgb),0.3)` }} />
                          ) : (
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: `rgba(var(--brand-primary-rgb),0.15)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--brand-primary)', fontWeight: 700 }}>
                              {(driver.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <span style={{ color: 'var(--on-surface)', fontWeight: idx < 3 ? 700 : 400 }}>{driver.name}</span>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', color: idx === 0 ? 'var(--brand-primary)' : 'var(--on-surface)', fontWeight: idx === 0 ? 700 : 400 }}>
                          {driver.points}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--on-surface-variant)', fontSize: 11 }}>
                Keine Fahrerdaten für {selectedYear}
              </div>
            )}
          </div>

          {/* ── Read-only notice ── */}
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--on-surface-variant)', opacity: 0.6, textAlign: 'center', letterSpacing: '0.08em' }}>
            READ-ONLY — ARCHIVIERT AM {archive ? new Date(archive.archived_at).toLocaleDateString('de-DE') : '—'}
          </div>
        </>
      )}
    </div>
  );
}
